import React, { FC, useEffect, useState } from 'react';
import { ArchivePageProps } from './ArchivePageProps';
import DefaultPageLayout from '../../common/DefaultPageLayout';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../store';
import {
  countriesSelector,
  getSelectedCountryCodes,
} from '../../store/selectData/selectors';
import { selectCountryCodes } from '../../store/selectData/actionWrappers';
import { getProjectsToFetch } from '../../utils/utils';
import ArchivePageSidebar from '../../components/ArchivePageSidebar';
import ArchivePageTable from '../../components/ArchivePageTable';
import {
  fetchProjects,
  unArchiveProjects,
} from '../../store/archive/actionWrappers';
import { selectLoading, selectProjects } from '../../store/archive/selectors';
import Button from '@ingka/button';
import ConfirmationModal from '../../common/ConfirmationModal';
import useTranslation from '../../hooks/useTranslation';
import { deleteProject } from '../../store/archive/actionWrappers';
import EditProjectModals from '../../components/EditProjectModals';
import { selectProjectToEditWithFormattedInvestments } from '../../store/projects/selectors';
import {
  clearSolutions,
  getProjectDetails,
} from '../../store/projects/actionWrapper';
import { projectsActions } from '../../store/projects/slice';

const ArchivePage: FC<ArchivePageProps> = () => {
  const dispatch = useDispatch();
  const t = useTranslation();
  const countryList = useTypedSelector(countriesSelector);
  const selectedCountryCodes = useTypedSelector(getSelectedCountryCodes);
  const projects = useTypedSelector(selectProjects);
  const loading = useTypedSelector(selectLoading);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [deleteProjectsModal, setDeleteProjectsModal] = useState(false);
  const [establishmentType, setEstablishmentType] = useState(null);
  const formattedProject = useTypedSelector(
    selectProjectToEditWithFormattedInvestments,
  );
  const [tableSelection, setTableSelection] = useState<string[]>([]);
  const tableSelectionChangedHandler = (selection: string[]) => {
    setTableSelection(selection);
  };
  const closeConfirmationModal = () => {
    setConfirmationModal(false);
  };
  const openConfirmationModal = () => {
    setConfirmationModal(true);
  };
  const closeDeleteProjectsModal = () => {
    setDeleteProjectsModal(false);
  };
  const handleOpenEditProjectModal = (projectId: string) => {
    dispatch(getProjectDetails({ id: projectId, dontAddToProjects: true }));
  };
  const closeEditModal = () => {
    setEstablishmentType(null);
    dispatch(projectsActions.clearProject());
    dispatch(clearSolutions());
  };
  const openDeleteProjectsModal = () => {
    setDeleteProjectsModal(true);
  };
  const deleteProjectHandler = () => {
    tableSelection.forEach((projectId) => {
      dispatch(deleteProject(projectId));
    });
    setTableSelection([]);
    closeDeleteProjectsModal();
  };
  const unArchiveProjectHandler = () => {
    dispatch(unArchiveProjects(tableSelection));
    closeConfirmationModal();
    setTableSelection([]);
  };
  useEffect(() => {
    if (formattedProject) {
      setEstablishmentType(formattedProject.establishmentType);
    }
  }, [formattedProject]);

  const onCountryCodeChange = (countryCodes: string[]) => {
    if (countryCodes.includes('All')) {
      dispatch(selectCountryCodes(countryList));
    } else {
      dispatch(
        selectCountryCodes(
          countryCodes.map((el) =>
            countryList.find((c) => c.countryName === el),
          ),
        ),
      );
    }
    if (countryCodes.length != 0) {
      dispatch(fetchProjects(getProjectsToFetch(countryCodes, countryList)));
    }
  };

  useEffect(() => {
    if (selectedCountryCodes.length > 0) {
      dispatch(fetchProjects(selectedCountryCodes.map((el) => el.countryId)));
    }
  }, [selectedCountryCodes]);

  return (
    <>
      <DefaultPageLayout
        sidebar={() => (
          <ArchivePageSidebar
            selectedCountryCodes={selectedCountryCodes}
            onCountryCodeChange={onCountryCodeChange}
            countryList={countryList}
          />
        )}
        mainPart={() => (
          <Box px={2} height="100%">
            <ArchivePageTable
              projects={projects}
              loading={loading}
              tableSelection={tableSelection}
              setTableSelection={tableSelectionChangedHandler}
              deleteProject={openDeleteProjectsModal}
              unArchiveProject={openConfirmationModal}
              onRowClick={handleOpenEditProjectModal}
            />
          </Box>
        )}
      />
      <ConfirmationModal
        visible={confirmationModal}
        closeHandler={closeConfirmationModal}
        title={'Are You Sure?'}
        text={'Are you sure you want to unarchive selected projects?'}
      >
        <>
          <Button
            style={{ width: '12.5rem', marginRight: '1.5rem' }}
            data-testid={'confirm-close-button'}
            type={'danger'}
            text={t('common.yesSure')}
            onClick={() => {
              unArchiveProjectHandler();
            }}
          />
        </>
      </ConfirmationModal>
      <ConfirmationModal
        visible={deleteProjectsModal}
        closeHandler={closeDeleteProjectsModal}
        title={'Are You Sure?'}
        text={'Are you sure you want to delete selected projects?'}
      >
        <>
          <Button
            style={{ width: '12.5rem', marginRight: '1.5rem' }}
            data-testid={'confirm-close-button'}
            type={'danger'}
            text={t('common.yesSure')}
            onClick={() => {
              deleteProjectHandler();
            }}
          />
        </>
      </ConfirmationModal>
      <EditProjectModals
        closeHandler={closeEditModal}
        establishmentType={establishmentType}
        project={formattedProject}
        projectsList={[]}
        countries={countryList}
        setActiveProject={() => {
          //DO NOTHING
        }}
        readonly
        submitTrigger={() => {
          //DO Nothing
        }}
      />
    </>
  );
};

export default ArchivePage;
